import { useState } from "react";
import { GREETINGS, pickGreeting } from "./greetings";

const KEY = "artie:greeting";
const TTL_MS = 30 * 60 * 1000; // rotate the greeting every 30 minutes

interface Stored {
  userId: string;
  greeting: string;
  pickedAt: number;
}

function readStored(): Stored | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Stored;
    if (
      typeof s?.greeting === "string" &&
      GREETINGS.includes(s.greeting) &&
      typeof s?.pickedAt === "number"
    ) {
      return s;
    }
    return null;
  } catch {
    return null;
  }
}

/** The greeting for the current 30-minute window. Picked once and kept stable
 *  across new chats, route changes, and refreshes (persisted in `localStorage`),
 *  then rotated to a fresh line once 30 minutes have elapsed. Re-picks when the
 *  user id changes (account switch) or when the stored line is no longer part of
 *  the library. */
export function useSessionGreeting(userId?: string | number | null): string {
  const [greeting] = useState<string>(() => {
    const uid = userId == null ? "anon" : String(userId);
    const stored = readStored();
    if (stored && stored.userId === uid && Date.now() - stored.pickedAt < TTL_MS) {
      return stored.greeting;
    }
    const picked = pickGreeting();
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify({ userId: uid, greeting: picked, pickedAt: Date.now() }),
      );
    } catch {
      /* storage unavailable — fall back to an in-memory pick for this mount */
    }
    return picked;
  });
  return greeting;
}
